'use client';

import { useTranslations } from 'next-intl';
import { HStack } from '@terra/ui-styled-system';
import { ViewIcon } from '@terra/ui-icons';
import type { MemoryRecordResponse } from '@terra/shared-contracts-memory';
import {
  canRemoveEndpointMemory,
  EndpointMemoryDeleteButton,
  EndpointMemoryEditButton,
  IconButton,
  Tooltip,
  toMemoryRecordResponse,
  type EndpointMemoryRow,
} from '@terra/ui-design-system';

type EndpointMemoriesContentActionsProps = {
  memory: EndpointMemoryRow;
  loading: boolean;
  onView: (memory: MemoryRecordResponse) => void;
  onEdit: (memory: MemoryRecordResponse) => void;
  onDelete: (memory: MemoryRecordResponse) => void;
};

export function EndpointMemoriesContentActions({
  memory,
  loading,
  onView,
  onEdit,
  onDelete,
}: EndpointMemoriesContentActionsProps) {
  const t = useTranslations('memories.endpointMemories');
  const status = memory.status ?? 'active';
  const statusAllowsEdit = status === 'active' || status === 'rejected';
  const canRemove = canRemoveEndpointMemory(memory);

  return (
    <HStack gap="1" justifyContent="center" alignItems="center" h="full">
      <Tooltip title={t('viewTooltip')}>
        <IconButton
          aria-label={t('viewAriaLabel')}
          size="xs"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            onView(toMemoryRecordResponse(memory));
          }}
        >
          <ViewIcon size={14} />
        </IconButton>
      </Tooltip>
      {statusAllowsEdit ? (
        <EndpointMemoryEditButton
          creationMethod={memory.creationMethod}
          loading={loading}
          onEdit={() => {
            onEdit(toMemoryRecordResponse(memory));
          }}
        />
      ) : null}
      <EndpointMemoryDeleteButton
        creationMethod={memory.creationMethod}
        status={memory.status}
        loading={loading}
        onDelete={() => {
          if (!canRemove) {
            return;
          }

          onDelete(toMemoryRecordResponse(memory));
        }}
      />
    </HStack>
  );
}
